import ms from 'ms'
import React from 'react'

import AccountMenu from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/AccountMenu/AccountMenu'
import SponsorCompany from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/CompanySponsor'
import CompassSponsorCompany from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/CompassSponsorCompany'
import DevLogo from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/DevLogo'
import LogoAndMenuToggleButton from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/LogoAndMenuToggleButton'
import QuickActionsButton from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/QuickActionsButton/QuickActionsButton'
import SidebarLinks from '@/components/layouts/DashboardLayout/components/DashboardSidebar/components/SidebarLinks'
import { useGlobalSearch } from '@/components/misc/GlobalSearch/hooks/useGlobalSearch'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useAppSelector from '@/hooks/useAppSelector'
import { useAuth } from '@/hooks/useAuth'
import { useCurrentCompany } from '@/hooks/useCurrentCompany'
import { useIsBreakpoint } from '@/hooks/useIsBreakpoint'
import useLocalStorage from '@/hooks/useLocalStorage'
import { BREAKPOINTS, LOCAL_STORAGE_KEYS } from '@/lib/constants'
import { isProduction } from '@/lib/helpers/envHelpers'
import { cn } from '@/lib/utils'
import { selectLatestCompassReport } from '@/redux/slices/clientPortal'
import {
  selectCompanySponsor,
  selectIsCompanyUser,
} from '@/redux/slices/session'
import { SidebarLink } from '@/types/misc'

interface Props {
  links: SidebarLink[]
}

const DashboardSidebar: React.FC<Props> = ({ links }) => {
  const isCompanyUser = useAppSelector(selectIsCompanyUser)
  const globalSearch = useGlobalSearch()
  const { isAuthenticated, isInitialized, isClientUser } = useAuth()
  const companySponsor = useAppSelector(selectCompanySponsor)
  const currentCompany = useCurrentCompany()
  const latestCompassReport = useAppSelector(selectLatestCompassReport)
  const isMdBreakpoint = useIsBreakpoint(BREAKPOINTS.md)
  const { isDesktop, isMobile, isCollapsedOnDesktop, open } = useSidebar()
  const shouldShowCollapseSidebarButton = isDesktop && open
  const shouldShowExpandSidebarButton = isDesktop && !open
  const [isShowingDevLogo, setIsShowingDevLogo] = useLocalStorage(
    LOCAL_STORAGE_KEYS.showDevLogo,
    !isProduction(),
  )

  // If we're showing the global search on the `md` breakpoint, we need to hide
  // the sidebar so that it doesn't overlap the global search.
  if (globalSearch.isShowing && isMdBreakpoint) {
    return null
  }

  // Hide the sponsor info when on desktop and showing the collapsed sidebar
  // because we don't have enough space
  const shouldShowSponsorInfo = isMobile || (isDesktop && open)

  const shouldShowCompanySponsor = !!companySponsor && shouldShowSponsorInfo
  const shouldShowClientSponsor =
    isClientUser && !!latestCompassReport && shouldShowSponsorInfo

  return (
    <Sidebar collapsible="icon" className="shadow">
      {shouldShowCollapseSidebarButton && (
        <Tooltip>
          <TooltipTrigger asChild>
            <SidebarTrigger className="absolute right-5 top-4" />
          </TooltipTrigger>

          <TooltipContent>Collapse sidebar</TooltipContent>
        </Tooltip>
      )}

      <SidebarHeader className="border-b border-border/40">
        <LogoAndMenuToggleButton />
      </SidebarHeader>

      <SidebarContent className={cn(isCollapsedOnDesktop ? 'pt-1' : 'pt-6')}>
        {shouldShowExpandSidebarButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarTrigger className="mx-auto" />
            </TooltipTrigger>

            <TooltipContent>Expand sidebar</TooltipContent>
          </Tooltip>
        )}
        <SidebarLinks links={links} />
      </SidebarContent>

      <SidebarFooter className="items-center gap-4">
        {shouldShowCompanySponsor && (
          <SponsorCompany sponsor={companySponsor} />
        )}

        {shouldShowClientSponsor && (
          <CompassSponsorCompany
            variant="plain"
            compassReport={latestCompassReport}
            className="px-6"
          />
        )}

        {(currentCompany || isShowingDevLogo) && (
          <div className="flex flex-col items-center gap-4">
            {isShowingDevLogo && (
              <DevLogo
                onClick={() => {
                  setIsShowingDevLogo(false)
                  setInterval(() => setIsShowingDevLogo(true), ms('2m'))
                }}
              />
            )}

            {currentCompany && isCompanyUser && <QuickActionsButton />}
          </div>
        )}

        {isInitialized && isAuthenticated && <AccountMenu />}
      </SidebarFooter>
    </Sidebar>
  )
}

export default DashboardSidebar
